import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { markManualThemeChange } from '@/hooks/useNetworkThemeSync';

interface SwatchProProps {
  theme?: string;
}

interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  cardBg: string;
  text: string;
  border: string;
  headerAccent: string;
}

interface AppTheme {
  name: string;
  mode: 'dark' | 'light';
  colors: ThemeColors;
}

// Convert CSS color (#rrggbb or rgb/rgba) to hex #rrggbb for <input type="color">
const cssColorToHex = (input: string): string => {
  const src = (input || '').trim();
  if (!src) return '#000000';
  if (src.startsWith('#')) {
    let clean = src.replace('#', '');
    if (clean.length === 3) clean = clean.split('').map(ch => ch + ch).join('');
    if (clean.length !== 6) return '#000000';
    return `#${clean.toLowerCase()}`;
  }
  const m = src.match(/^rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map(v => parseFloat(v.trim()));
    const r = Math.max(0, Math.min(255, parts[0] || 0));
    const g = Math.max(0, Math.min(255, parts[1] || 0));
    const b = Math.max(0, Math.min(255, parts[2] || 0));
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return '#000000';
};

// Convert CSS color (#rrggbb or rgb/rgba) to HSL tuple string: "h s% l%"
const hexToHslTuple = (input: string): string => {
  let rN = 0, gN = 0, bN = 0;
  const src = (input || '').trim();
  if (!src) return '0 0% 100%';
  if (/^rgba?\(/i.test(src)) {
    const m = src.match(/^rgba?\(([^)]+)\)/i);
    if (m) {
      const parts = m[1].split(',').map(v => parseFloat(v.trim()));
      rN = Math.max(0, Math.min(255, parts[0] || 0));
      gN = Math.max(0, Math.min(255, parts[1] || 0));
      bN = Math.max(0, Math.min(255, parts[2] || 0));
    }
  } else {
    let clean = src.replace('#', '');
    if (clean.length === 3) clean = clean.split('').map(ch => ch + ch).join('');
    if (clean.length === 6) {
      rN = parseInt(clean.substring(0, 2), 16);
      gN = parseInt(clean.substring(2, 4), 16);
      bN = parseInt(clean.substring(4, 6), 16);
    }
  }
  const r = rN / 255, g = gN / 255, b = bN / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
};

const PRESET_THEME_LOGOS = {
  'mdbapp-default': '/Dynamic Header Logos/MdbApp.io Default Logo.png',
  baller: '/Dynamic Header Logos/Mdbapp.io Baller Logo.png',
  matrix: '/Dynamic Header Logos/MdbApp.io Matrix Logo.png',
  'king-blue': '/Dynamic Header Logos/MdbApp.io King Blue Logo.png',
  blade: '/Dynamic Header Logos/MdbApp.io Blade Logo.png',
  don: '/Dynamic Header Logos/MdbApp.io Don Logo.png',
  ocean: '/Dynamic Header Logos/MdbApp.io Ocean Logo.png',
  aqua: '/Dynamic Header Logos/MdbApp.io Aqua Logo.png',
  smoke: '/Dynamic Header Logos/MdbApp.io Smoke Logo.png',
  sunset: '/Dynamic Header Logos/MdbApp.io Sunset Logo.png',
  // Network-themed presets (reuse NetworkSwitch logos)
  avx: '/Crypto Logos/AVAX.png',
  abstract: '/Crypto Logos/Abstract-logo.png',
  eth: '/Crypto Logos/ETH.png',
  base: '/Crypto Logos/BASE.png',
  bscchain: '/Crypto Logos/BNB_chain.png',
  poly: '/Crypto Logos/MATIC.png',
  op: '/Crypto Logos/OP.png',
  // Cyberpunk theme
  cbrpnk: '/Dynamic Header Logos/MdbApp.io CbrPnk Logo.png',
  cbrpnk2: '/Dynamic Header Logos/MdbApp.io CbrPnkII Logo.png',
  cbrpnk3: '/Dynamic Header Logos/MdbApp.io CbrPnkIII Logo.png'
};

const SwatchPro: React.FC<SwatchProProps> = ({ theme = 'dark' }) => {
  const { theme: appTheme, setTheme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentAppTheme, setCurrentAppTheme] = useState('dark');
  const [editingMode, setEditingMode] = useState<'dark' | 'light'>('dark');
  const [customColors, setCustomColors] = useState<{ dark: ThemeColors; light: ThemeColors }>({
    dark: {
      primary: '#0A74FF',
      secondary: '#10b981',
      background: '#000000',
      cardBg: '#1a1a1a',
      text: '#e5e5e5',
      border: '#333333',
      headerAccent: '#0A74FF'
    },
    light: {
      primary: '#2563eb',
      secondary: '#059669',
      background: '#ffffff',
      cardBg: '#f8fafc',
      text: '#1e293b',
      border: '#e2e8f0',
      headerAccent: '#2563eb'
    }
  });
  const [shimmeringTheme, setShimmeringTheme] = useState<string | null>(null);
  const [backgroundEffect, setBackgroundEffect] = useState(() => {
    return localStorage.getItem('backgroundEffect') || 'plasmasmoke';
  });
  const [currentThemeName, setCurrentThemeName] = useState(() => {
    return localStorage.getItem('currentThemeName') || '';
  });

  // Keep SwatchPro editing mode in sync with global app theme
  useEffect(() => {
    if (appTheme === 'light' || appTheme === 'dark') {
      setEditingMode(appTheme);
    }
  }, [appTheme]);

  // When global theme changes via header, re-apply user's custom theme palette for that mode
  useEffect(() => {
    if (appTheme !== 'light' && appTheme !== 'dark') return;
    try {
      const saved = localStorage.getItem('userAppCustomTheme');
      if (!saved) return;
      const data = JSON.parse(saved);
      const mode = appTheme as 'light' | 'dark';
      const palette = (data.colors && (data.colors[mode])) || customColors[mode];
      if (palette) {
        const normalized = normalizeColorsForMode(mode, palette);
        applyCustomThemeToMainApp(mode, normalized);
      }
    } catch (_) { /* no-op */ }
  }, [appTheme]);

  // Add shimmer animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Load saved theme from localStorage
  useEffect(() => {
    try {
      const mainAppTheme = localStorage.getItem('theme') || 'dark';
      setCurrentAppTheme(mainAppTheme);
      setEditingMode(mainAppTheme as 'dark' | 'light');
      
      const savedCustomTheme = localStorage.getItem('userAppCustomTheme');
      if (savedCustomTheme) {
        const themeData = JSON.parse(savedCustomTheme);
        const isPerMode = themeData?.colors && (themeData.colors.dark || themeData.colors.light);
        const isSinglePalette = themeData?.colors && themeData.colors.primary && themeData.colors.text;

        if (isPerMode) {
          // New schema: { colors: { dark: ThemeColors, light: ThemeColors } }
          setCustomColors(themeData.colors);
          const rawColors = themeData.colors[mainAppTheme as 'dark' | 'light'] || themeData.colors.dark;
          if (rawColors) {
            const colorsToApply = normalizeColorsForMode(mainAppTheme as 'dark' | 'light', rawColors);
            try {
              const normalizedData = { ...themeData, colors: { ...themeData.colors, [mainAppTheme]: colorsToApply } };
              localStorage.setItem('userAppCustomTheme', JSON.stringify(normalizedData));
            } catch (_) { /* no-op */ }
            setTimeout(() => {
              applyCustomThemeToMainApp(mainAppTheme as 'dark' | 'light', colorsToApply);
            }, 100);
          }
        } else if (isSinglePalette) {
          // Legacy schema: { colors: ThemeColors }
          const palette = themeData.colors as ThemeColors;
          // Apply to current mode and upgrade storage to per-mode without losing other mode
          setCustomColors(prev => {
            const updated = { ...prev, [mainAppTheme]: palette };
            try {
              localStorage.setItem('userAppCustomTheme', JSON.stringify({ colors: updated, timestamp: Date.now() }));
            } catch (_) { /* no-op */ }
            setTimeout(() => {
              applyCustomThemeToMainApp(mainAppTheme as 'dark' | 'light', normalizeColorsForMode(mainAppTheme as 'dark' | 'light', palette));
            }, 100);
            return updated;
          });
        }
      }
    } catch (error) {
      console.log('No saved user app theme found');
    }
  }, []);

  // Helper function to trigger shimmer effect
  const triggerShimmer = (identifier: string) => {
    setShimmeringTheme(identifier);
    setTimeout(() => setShimmeringTheme(null), 800);
  };

  // Helper function to get shimmer color for themes
  const getShimmerColor = (themeName: string) => {
    switch (themeName) {
      case 'AIFC Dark':
      case 'AIFC Light':
        return 'rgba(10, 116, 255, 0.6)';
      default:
        return 'rgba(255, 255, 255, 0.4)';
    }
  };

  // Apply custom theme colors
  const applyCustomThemeToMainApp = (baseTheme: 'dark' | 'light', colors: ThemeColors) => {
    localStorage.setItem('theme', baseTheme);
    document.documentElement.setAttribute('data-theme', baseTheme);
    
    // Special handling for Base theme in light mode
    // In light mode, the white primary becomes invisible, so use text color (blue) instead
    const currentThemeName = localStorage.getItem('currentThemeName') || '';
    const isBaseThemeInLightMode = (currentThemeName.toLowerCase() === 'base') && baseTheme === 'light';
    const effectivePrimary = isBaseThemeInLightMode ? colors.text : colors.headerAccent;
    
    const root = document.documentElement;
    root.style.setProperty('--color-primary', effectivePrimary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-card-bg', colors.cardBg);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-header-accent', effectivePrimary);
    // Ensure app-wide text utilities (e.g., Tailwind/shadcn text-foreground) follow custom theme
    const textHsl = hexToHslTuple(colors.text);
    root.style.setProperty('--foreground', textHsl);
    root.style.setProperty('--muted-foreground', textHsl);
    // For components using --text-color directly
    root.style.setProperty('--text-color', colors.text);
  };

  // Ensure colors align with the selected mode to avoid hybrid visuals
  const normalizeColorsForMode = (mode: 'dark' | 'light', colors: ThemeColors): ThemeColors => {
    if (mode === 'light') {
      return {
        primary: colors.primary,
        secondary: colors.secondary,
        // Force a light surface while keeping accents
        background: '#ffffff',
        cardBg: '#f8fafc',
        // Keep the user-selected text color across modes
        text: colors.text,
        border: '#e2e8f0',
        headerAccent: colors.headerAccent || colors.primary
      };
    }
    // Dark mode - use provided colors as-is
    return colors;
  };

  // Clear custom theme styles
  const clearCustomThemeStyles = () => {
    const root = document.documentElement;
    const body = document.body;
    
    root.style.removeProperty('--color-primary');
    root.style.removeProperty('--color-secondary');
    root.style.removeProperty('--color-background');
    root.style.removeProperty('--color-card-bg');
    root.style.removeProperty('--color-text');
    root.style.removeProperty('--color-border');
    root.style.removeProperty('--color-header-accent');
    root.style.removeProperty('--foreground');
    root.style.removeProperty('--muted-foreground');
    root.style.removeProperty('--text-color');
    // Do not override body styles; rely on global theme and CSS variables
  };

  // Use default theme
  const useDefaultTheme = (themeMode: 'dark' | 'light') => {
    localStorage.setItem('theme', themeMode);
    document.documentElement.setAttribute('data-theme', themeMode);
    setCurrentAppTheme(themeMode);
    
    clearCustomThemeStyles();
    localStorage.removeItem('userAppCustomTheme');

    try {
      window.dispatchEvent(new CustomEvent('userAppThemeChanged', {
        detail: { mode: themeMode, colors: null }
      }));
    } catch (_) { /* no-op */ }
  };

  // Save and apply custom theme
  const saveAndApplyCustomTheme = (themeMode: 'dark' | 'light', colors: ThemeColors) => {
    try {
      // Mark this as a manual theme change to prevent auto-switching
      markManualThemeChange();
      
      // Normalize colors to the target mode before saving/applying
      const modeSafeColors = normalizeColorsForMode(themeMode, colors);
      // Merge into per-mode map so we don't wipe the other mode
      let existing: any = null;
      try { existing = JSON.parse(localStorage.getItem('userAppCustomTheme') || 'null'); } catch (_) { existing = null; }
      const existingColors = (existing && existing.colors && (existing.colors.dark || existing.colors.light))
        ? existing.colors
        : { [themeMode]: modeSafeColors };
      const mergedColors = { ...existingColors, [themeMode]: modeSafeColors } as { dark?: ThemeColors; light?: ThemeColors };
      localStorage.setItem('userAppCustomTheme', JSON.stringify({ colors: mergedColors, timestamp: Date.now() }));
      
      localStorage.setItem('theme', themeMode);
      document.documentElement.setAttribute('data-theme', themeMode);
      
      applyCustomThemeToMainApp(themeMode, modeSafeColors);
      setCurrentAppTheme(themeMode);
      
      setCustomColors(prev => ({ ...prev, [themeMode]: modeSafeColors }));
      
      window.dispatchEvent(new CustomEvent('userAppThemeChanged', { 
        detail: { mode: themeMode, colors: modeSafeColors } 
      }));
    } catch (error) {
      console.log('Error saving custom theme');
    }
  };

  // Predefined app themes - using MdbApp's current theme as defaults
  const appThemes: AppTheme[] = [
    // Intentionally empty: remove AIFC Dark/Light presets
  ];

  // Function to get the current active app theme name
  const getCurrentAppThemeName = () => {
    const modeToCheck = editingMode || currentAppTheme;
    const currentColors = customColors[modeToCheck as 'dark' | 'light'];
    
    if (!currentColors) {
      return modeToCheck === 'light' ? 'Light' : 'Dark';
    }
    
    for (const appTheme of appThemes) {
      const colorsMatch = 
        appTheme.colors.primary === currentColors.primary &&
        appTheme.colors.secondary === currentColors.secondary &&
        appTheme.colors.background === currentColors.background &&
        appTheme.colors.cardBg === currentColors.cardBg &&
        appTheme.colors.text === currentColors.text &&
        appTheme.colors.border === currentColors.border &&
        appTheme.colors.headerAccent === currentColors.headerAccent;
      
      if (colorsMatch) {
        return appTheme.name;
      }
    }
    
    return modeToCheck === 'light' ? 'Light' : 'Dark';
  };

  const resetTheme = () => {
    useDefaultTheme('dark');
    setCustomColors({
      dark: {
        primary: '#0A74FF',
        secondary: '#10b981',
        background: '#000000',
        cardBg: '#1a1a1a',
        text: '#e5e5e5',
        headerAccent: '#0A74FF',
        border: '#333333'
      },
      light: {
        primary: '#2563eb',
        secondary: '#059669',
        background: '#ffffff',
        cardBg: '#f8fafc',
        text: '#1e293b',
        border: '#e2e8f0',
        headerAccent: '#2563eb'
      }
    });
    setEditingMode('dark');
  };

  // Helper function to get current editing colors
  const getCurrentEditingColors = () => {
    const colors = customColors[editingMode];
    // Fallback to default colors if undefined to prevent crashes
    if (!colors) {
      return editingMode === 'light' ? {
        primary: '#2563eb',
        secondary: '#059669',
        background: '#ffffff',
        cardBg: '#f8fafc',
        text: '#1e293b',
        border: '#e2e8f0',
        headerAccent: '#2563eb'
      } : {
        primary: '#0A74FF',
        secondary: '#10b981',
        background: '#000000',
        cardBg: '#1a1a1a',
        text: '#e5e5e5',
        border: '#333333',
        headerAccent: '#0A74FF'
      };
    }
    return colors;
  };

  return (
    <div 
      data-swatch-pro
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--color-header-accent, #0A74FF)',
        borderRadius: '12px',
        padding: '1.5rem',
        position: 'relative'
      }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
      }}
      onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, justifyContent: 'center' }}>
          <img 
            src="/Pallette Icons/Color Pallette Dark Mode.png" 
            alt="Palette" 
            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            className="dark:block hidden"
          />
          <img 
            src="/Pallette Icons/Color Pallette Light Mode.png" 
            alt="Palette" 
            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            className="dark:hidden block"
          />
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-color)'
          }}>
            Swatch Pro
          </h3>
          <img 
            src="/Pallette Icons/Color Pallette Dark Mode.png" 
            alt="Palette" 
            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            className="dark:block hidden"
          />
          <img 
            src="/Pallette Icons/Color Pallette Light Mode.png" 
            alt="Palette" 
            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            className="dark:hidden block"
          />
        </div>
        <button
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-color)',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Main App Theme Customizer Card */}
          <div style={{
            background: 'transparent',
            border: `1px solid ${customColors[currentAppTheme as 'dark' | 'light']?.headerAccent || 'var(--color-border)'}`,
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <h4 style={{ 
              color: 'var(--text-color)', 
              marginBottom: '1rem', 
              fontSize: '16px',
              fontWeight: '600',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <div 
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: getCurrentEditingColors().headerAccent,
                  border: `2px solid ${getCurrentEditingColors().border}`
                }}
              />
              Customize App Theme
            </h4>
            
            {/* Light/Dark Mode Toggle */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h5 style={{ 
                color: 'var(--text-color)', 
                marginBottom: '0.75rem', 
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                Editing Mode
              </h5>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center',
                marginBottom: '0.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'rgba(0, 0, 0, 0.3)'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingMode('dark');
                      setTheme('dark');
                      if (customColors.dark && currentAppTheme !== 'dark') {
                        saveAndApplyCustomTheme('dark', customColors.dark);
                      }
                    }}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      background: appTheme === 'dark' 
                        ? `linear-gradient(135deg, ${customColors.dark?.headerAccent}33 0%, ${customColors.dark?.headerAccent}1a 100%)` 
                        : 'rgba(128, 128, 128, 0.2)',
                      color: appTheme === 'dark' 
                        ? customColors.dark?.headerAccent || '#10b981' 
                        : customColors.dark?.headerAccent || '#666',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      minWidth: '120px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {appTheme === 'dark' && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 0.8s ease-out'
                      }} />
                    )}
                    <Moon size={16} />
                    Dark Mode
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingMode('light');
                      setTheme('light');
                      if (customColors.light && currentAppTheme !== 'light') {
                        saveAndApplyCustomTheme('light', customColors.light);
                      }
                    }}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      background: appTheme === 'light' 
                        ? `linear-gradient(135deg, ${customColors.light?.headerAccent}33 0%, ${customColors.light?.headerAccent}1a 100%)` 
                        : 'rgba(128, 128, 128, 0.2)',
                      color: appTheme === 'light' 
                        ? customColors.light?.headerAccent || '#10b981' 
                        : customColors.light?.headerAccent || '#666',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      minWidth: '120px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {appTheme === 'light' && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 0.8s ease-out'
                      }} />
                    )}
                    <Sun size={16} />
                    Light Mode
                  </button>
                </div>
              </div>
              <p style={{ 
                fontSize: '11px', 
                color: '#666', 
                margin: 0,
                lineHeight: '1.4',
                textAlign: 'center'
              }}>
                Choose which mode to customize. Your changes will be saved for {editingMode} mode.
              </p>
            </div>

            {/* App Theme Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {appThemes.map((appTheme, index) => {
                const isDisabled = (editingMode === 'dark' && appTheme.name === 'AIFC Light') || 
                                  (editingMode === 'light' && appTheme.name === 'AIFC Dark');
                
                return (
                  <button
                    key={index}
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      triggerShimmer(appTheme.name);
                      if (appTheme.name === 'AIFC Dark' || appTheme.name === 'AIFC Light') {
                        useDefaultTheme(appTheme.mode);
                        setEditingMode(appTheme.mode);
                        setCustomColors(prev => ({
                          ...prev,
                          [appTheme.mode]: appTheme.colors
                        }));
                      } else {
                        saveAndApplyCustomTheme(editingMode, appTheme.colors);
                        setCustomColors(prev => ({
                          ...prev,
                          [editingMode]: appTheme.colors
                        }));
                      }
                    }}
                    style={{
                      padding: '12px',
                      background: getCurrentAppThemeName() === appTheme.name
                        ? `linear-gradient(135deg, ${appTheme.colors.headerAccent}33 0%, ${appTheme.colors.headerAccent}1a 100%)`
                        : 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${getCurrentAppThemeName() === appTheme.name ? appTheme.colors.headerAccent : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '8px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: isDisabled ? '#666' : (getCurrentAppThemeName() === appTheme.name ? appTheme.colors.headerAccent : 'var(--text-color)'),
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: isDisabled ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {shimmeringTheme === appTheme.name && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(90deg, transparent, ${getShimmerColor(appTheme.name)}, transparent)`,
                        animation: 'shimmer 0.8s ease-out'
                      }} />
                    )}
                    <div 
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: appTheme.colors.headerAccent,
                        border: `2px solid ${appTheme.colors.border}`
                      }}
                    />
                    {appTheme.name}
                  </button>
                );
              })}
            </div>

            {/* Custom Theme Selector */}
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{
                margin: '0 0 12px 0',
                color: 'var(--color-primary, #0A74FF)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Preset Themes
              </h5>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginBottom: '12px'
              }}>
                {/* Row 1 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Trigger shimmer effect
                    setShimmeringTheme('mdbapp-default');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // MdbApp Default theme (uses previous Default Dark colors)
                    const colors = {
                      primary: '#0a74ff',
                      secondary: '#10b981',
                      background: '#000000',
                      cardBg: '#1a1a1a',
                      text: '#e5e5e5',
                      border: '#333333',
                      headerAccent: '#0a74ff'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    // Keep currentThemeName aligned with Header preset theme name
                    setCurrentThemeName('MdbApp Default');
                    localStorage.setItem('currentThemeName', 'MdbApp Default');
                    // Also set background effect to Starfield
                    setBackgroundEffect('starfield');
                    localStorage.setItem('backgroundEffect', 'starfield');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'starfield' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: currentThemeName === 'MdbApp Default' ? 'rgba(10, 116, 255, 0.3)' : 'rgba(10, 116, 255, 0.15)',
                    border: currentThemeName === 'MdbApp Default' ? '3px solid #0a74ff' : '2px solid #0a74ff',
                    borderRadius: '8px',
                    boxShadow: currentThemeName === 'mdbapp-default' ? '0 0 0 2px rgba(10, 116, 255, 0.5)' : '0 0 0 1px rgba(10, 116, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#0a74ff',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'mdbapp-default' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['mdbapp-default'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['mdbapp-default']}
                      alt="MdbApp Default"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#0a74ff'
                    }} />
                  )}
                  MdbApp Default
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('baller');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Baller theme
                    const colors = {
                      primary: '#FAD000',
                      secondary: '#FFBB00',
                      background: '#000000',
                      cardBg: '#1a1200',
                      text: '#CC9600',
                      border: '#7A5A00',
                      headerAccent: '#FAD000'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    // Keep currentThemeName aligned with Header preset theme name
                    setCurrentThemeName('Baller');
                    localStorage.setItem('currentThemeName', 'Baller');
                    
                    // Also set background effect to Plasma
                    setBackgroundEffect('plasma');
                    localStorage.setItem('backgroundEffect', 'plasma');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasma' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: currentThemeName === 'Baller' ? 'rgba(250, 208, 0, 0.3)' : 'rgba(250, 208, 0, 0.15)',
                    border: currentThemeName === 'Baller' ? '3px solid #FAD000' : '2px solid #FAD000',
                    borderRadius: '8px',
                    boxShadow: currentThemeName === 'Baller' ? '0 0 0 2px rgba(250, 208, 0, 0.5)' : '0 0 0 1px rgba(250, 208, 0, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FAD000',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'baller' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['baller'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['baller']}
                      alt="Baller"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#FAD000'
                    }} />
                  )}
                  Baller
                </button>

                {/* Row 2 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('matrix');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Matrix theme
                    const colors = {
                      primary: '#008F00',
                      secondary: '#1E711E',
                      background: '#000000',
                      cardBg: '#001100',
                      text: '#009E00',
                      border: '#003300',
                      headerAccent: '#008F00'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Matrix');
                    localStorage.setItem('currentThemeName', 'Matrix');
                    
                    // Also set background effect to Matrix
                    setBackgroundEffect('matrix');
                    localStorage.setItem('backgroundEffect', 'matrix');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'matrix' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0, 143, 0, 0.15)',
                    border: '2px solid #008F00',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(0, 143, 0, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#008F00',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'matrix' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['matrix'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['matrix']}
                      alt="Matrix"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#008F00'
                    }} />
                  )}
                  Matrix
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('king-blue');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // King Blue theme
                    const colors = {
                      primary: '#0A74FF',
                      secondary: '#0A74FF',
                      background: '#000000',
                      cardBg: '#001133',
                      text: '#666666',
                      border: '#0A74FF',
                      headerAccent: '#0A74FF'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('King Blue');
                    localStorage.setItem('currentThemeName', 'King Blue');
                    
                    // Also set background effect to Plasma
                    setBackgroundEffect('plasma');
                    localStorage.setItem('backgroundEffect', 'plasma');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasma' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(10, 116, 255, 0.15)',
                    border: '2px solid #0A74FF',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(10, 116, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#0A74FF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'king-blue' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['king-blue'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['king-blue']}
                      alt="King Blue"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#0A74FF'
                    }} />
                  )}
                  King Blue
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('blade');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Blade theme
                    const colors = {
                      primary: '#FF0000',
                      secondary: '#FF0000',
                      background: '#000000',
                      cardBg: '#330000',
                      text: '#707070',
                      border: '#FF0000',
                      headerAccent: '#FF0000'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Blade');
                    localStorage.setItem('currentThemeName', 'Blade');
                    
                    // Also set background effect to Plasma
                    setBackgroundEffect('plasma');
                    localStorage.setItem('backgroundEffect', 'plasma');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasma' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 0, 0, 0.15)',
                    border: '2px solid #FF0000',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(255, 0, 0, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FF0000',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'blade' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['blade'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['blade']}
                      alt="Blade"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#FF0000'
                    }} />
                  )}
                  Blade
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('don');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Don theme
                    const colors = {
                      primary: '#6600FF',
                      secondary: '#6600FF',
                      background: '#000000',
                      cardBg: '#140a1f',
                      text: '#00FF4C',
                      border: '#6600FF',
                      headerAccent: '#6600FF'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Don');
                    localStorage.setItem('currentThemeName', 'Don');
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(102, 0, 255, 0.15)',
                    border: '2px solid rgb(102, 0, 255)',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(102, 0, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'rgb(102, 0, 255)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'don' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['don'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['don']}
                      alt="Don"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgb(102, 0, 255)'
                    }} />
                  )}
                  Don
                </button>

                {/* Row 3 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('ocean');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Ocean theme
                    const colors = {
                      primary: '#06b6d4',
                      secondary: '#0891b2',
                      background: '#0c1821',
                      cardBg: '#164e63',
                      text: '#cffafe',
                      border: '#0e7490',
                      headerAccent: '#06b6d4'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Ocean');
                    localStorage.setItem('currentThemeName', 'Ocean');
                    
                    // Also set background effect to Plasma
                    setBackgroundEffect('plasma');
                    localStorage.setItem('backgroundEffect', 'plasma');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasma' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(6, 182, 212, 0.15)',
                    border: '2px solid #06b6d4',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(6, 182, 212, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#06b6d4',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'ocean' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['ocean'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['ocean']}
                      alt="Ocean"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#06b6d4'
                    }} />
                  )}
                  Ocean
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('sunset');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Sunset theme
                    const colors = {
                      primary: '#f97316',
                      secondary: '#ea580c',
                      background: '#1c1917',
                      cardBg: '#292524',
                      text: '#fed7aa',
                      border: '#78716c',
                      headerAccent: '#f97316'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Sunset');
                    localStorage.setItem('currentThemeName', 'Sunset');
                    
                    // Also set background effect to Plasma
                    setBackgroundEffect('plasma');
                    localStorage.setItem('backgroundEffect', 'plasma');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasma' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(249, 115, 22, 0.15)',
                    border: '2px solid #f97316',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(249, 115, 22, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#f97316',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'sunset' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['sunset'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['sunset']}
                      alt="Sunset"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#f97316'
                    }} />
                  )}
                  Sunset
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('smoke');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Smoke theme
                    const colors = {
                      primary: '#5e5e5e',
                      secondary: '#5e5e5e',
                      background: '#0c1821',
                      cardBg: '#15232f',
                      text: '#eff5f6',
                      border: '#5e5e5e',
                      headerAccent: '#5e5e5e'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Smoke');
                    localStorage.setItem('currentThemeName', 'Smoke');
                    
                    // Also set background effect to Plasma Smoke
                    setBackgroundEffect('plasmasmoke');
                    localStorage.setItem('backgroundEffect', 'plasmasmoke');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'plasmasmoke' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(94, 94, 94, 0.15)',
                    border: '2px solid #5e5e5e',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(94, 94, 94, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#5e5e5e',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'smoke' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['smoke'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['smoke']}
                      alt="Smoke"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#5e5e5e'
                    }} />
                  )}
                  Smoke
                </button>

                {/* Row 4 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('cbrpnk');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // CbrPnk theme
                    const colors = {
                      primary: '#0084FF',
                      secondary: '#BB00FF',
                      background: '#000000',
                      cardBg: '#1a0a1a',
                      text: '#EC5CFF',
                      border: '#330033',
                      headerAccent: '#0084FF'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('CbrPnk');
                    localStorage.setItem('currentThemeName', 'CbrPnk');
                    
                    // Also set background effect to Matrix
                    setBackgroundEffect('matrix');
                    localStorage.setItem('backgroundEffect', 'matrix');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'matrix' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0, 132, 255, 0.15)',
                    border: '2px solid #0084FF',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(0, 132, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#0084FF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'cbrpnk' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['cbrpnk'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['cbrpnk']}
                      alt="CbrPnk"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#0084FF'
                    }} />
                  )}
                  CbrPnk
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('cbrpnk2');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // CbrPnkII theme
                    const colors = {
                      primary: '#ff0080',
                      secondary: '#00ffff',
                      background: '#0a0a0a',
                      cardBg: '#1a0a1a',
                      text: '#ff00ff',
                      border: '#330033',
                      headerAccent: '#ff0080'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('CbrPnkII');
                    localStorage.setItem('currentThemeName', 'CbrPnkII');
                    
                    // Also set background effect to Matrix
                    setBackgroundEffect('matrix');
                    localStorage.setItem('backgroundEffect', 'matrix');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'matrix' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 0, 128, 0.15)',
                    border: '2px solid #ff0080',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(255, 0, 128, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#ff0080',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'cbrpnk2' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['cbrpnk2'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['cbrpnk2']}
                      alt="CbrPnkII"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#ff0080'
                    }} />
                  )}
                  CbrPnkII
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('cbrpnk3');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // CbrPnkIII theme
                    const colors = {
                      primary: '#0084FF',
                      secondary: '#BB00FF',
                      background: '#000000',
                      cardBg: '#1a0a1a',
                      text: '#00FBFF',
                      border: '#330033',
                      headerAccent: '#0084FF'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('CbrPnkIII');
                    localStorage.setItem('currentThemeName', 'CbrPnkIII');
                    
                    // Also set background effect to Matrix
                    setBackgroundEffect('matrix');
                    localStorage.setItem('backgroundEffect', 'matrix');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'matrix' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0, 132, 255, 0.15)',
                    border: '2px solid #0084FF',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(0, 132, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#0084FF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'cbrpnk3' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['cbrpnk3'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['cbrpnk3']}
                      alt="CbrPnkIII"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#0084FF'
                    }} />
                  )}
                  CbrPnkIII
                </button>

                {/* Row 5 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('aqua');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Aqua theme
                    const colors = {
                      primary: '#00D1AE',
                      secondary: '#00CED1',
                      background: '#000000',
                      cardBg: '#1a1a1a',
                      text: '#737373',
                      border: '#00D1AE',
                      headerAccent: '#00D1AE'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Aqua');
                    localStorage.setItem('currentThemeName', 'Aqua');
                    
                    // Also set background effect to Bubbles
                    setBackgroundEffect('bubbles');
                    localStorage.setItem('backgroundEffect', 'bubbles');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'bubbles' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0, 209, 174, 0.15)',
                    border: '2px solid #00D1AE',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(0, 209, 174, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#00D1AE',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'aqua' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['aqua'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['aqua']}
                      alt="Aqua"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#00D1AE'
                    }} />
                  )}
                  Aqua
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('bscchain');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // BSCChain theme
                    const colors = {
                      primary: '#F3BA2F',
                      secondary: '#F3BA2F',
                      background: '#000000',
                      cardBg: '#1a1400',
                      text: '#FFFFFF',
                      border: '#F3BA2F',
                      headerAccent: '#F3BA2F'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('BSCChain');
                    localStorage.setItem('currentThemeName', 'BSCChain');
                    
                    // Also set background effect to Hexagon Grid
                    setBackgroundEffect('hexagongrid');
                    localStorage.setItem('backgroundEffect', 'hexagongrid');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'hexagongrid' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(243, 186, 47, 0.15)',
                    border: '2px solid #F3BA2F',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(243, 186, 47, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#F3BA2F',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'bscchain' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['bscchain'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['bscchain']}
                      alt="BSCChain"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#F3BA2F'
                    }} />
                  )}
                  BSCChain
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('avx');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // AVX theme
                    const colors = {
                      primary: '#E84142',
                      secondary: '#E84142',
                      background: '#000000',
                      cardBg: '#1a0a0a',
                      text: '#FFFFFF',
                      border: '#E84142',
                      headerAccent: '#E84142'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('AVX');
                    localStorage.setItem('currentThemeName', 'AVX');
                    
                    // Also set background effect to Snow
                    setBackgroundEffect('snow');
                    localStorage.setItem('backgroundEffect', 'snow');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'snow' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(232, 65, 66, 0.15)',
                    border: '2px solid #E84142',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(232, 65, 66, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#E84142',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'avx' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['avx'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['avx']}
                      alt="AVAX"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#E84142'
                    }} />
                  )}
                  AVX
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('abstract');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Abstract theme
                    const colors = {
                      primary: '#34DA84',
                      secondary: '#34DA84',
                      background: '#000000',
                      cardBg: '#0f1a14',
                      text: '#FFFFFF',
                      border: '#34DA84',
                      headerAccent: '#34DA84'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('Abstract');
                    localStorage.setItem('currentThemeName', 'Abstract');
                    
                    // Also set background effect to Neural Network
                    setBackgroundEffect('neuralnetwork');
                    localStorage.setItem('backgroundEffect', 'neuralnetwork');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'neuralnetwork' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(52, 218, 132, 0.15)',
                    border: '2px solid #34DA84',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(52, 218, 132, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#34DA84',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'abstract' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['abstract'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['abstract']}
                      alt="Abstract"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#34DA84'
                    }} />
                  )}
                  Abstract
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('eth');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // ETH theme
                    const colors = {
                      primary: '#3C3C3D',
                      secondary: '#3C3C3D',
                      background: '#000000',
                      cardBg: '#1a1a1a',
                      text: '#FFFFFF',
                      border: '#3C3C3D',
                      headerAccent: '#3C3C3D'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    setCurrentThemeName('ETH');
                    localStorage.setItem('currentThemeName', 'ETH');
                    
                    // Also set background effect to Constellation
                    setBackgroundEffect('constellation');
                    localStorage.setItem('backgroundEffect', 'constellation');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'constellation' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(60, 60, 61, 0.15)',
                    border: '2px solid #3C3C3D',
                    borderRadius: '8px',
                    boxShadow: '0 0 0 1px rgba(60, 60, 61, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#c9c9cb',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'eth' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['eth'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['eth']}
                      alt="ETH"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#c9c9cb'
                    }} />
                  )}
                  ETH
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('base');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Base theme
                    const colors = {
                      primary: '#FFFFFF',
                      secondary: '#0A74FF',
                      background: '#FFFFFF',
                      cardBg: '#f8fafc',
                      text: '#0A74FF',
                      border: '#e2e8f0',
                      headerAccent: '#FFFFFF'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    // Keep currentThemeName aligned with Header preset theme name
                    setCurrentThemeName('Base');
                    localStorage.setItem('currentThemeName', 'Base');
                    
                    // Also set background effect to Geometric
                    setBackgroundEffect('geometric');
                    localStorage.setItem('backgroundEffect', 'geometric');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'geometric' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: currentThemeName === 'Base' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                    border: currentThemeName === 'Base' ? '3px solid #FFFFFF' : '2px solid #FFFFFF',
                    borderRadius: '8px',
                    boxShadow: currentThemeName === 'base' ? '0 0 0 2px rgba(255, 255, 255, 0.5)' : '0 0 0 1px rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FFFFFF',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'base' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['base'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['base']}
                      alt="Base"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      border: '1px solid #0A74FF'
                    }} />
                  )}
                  Base
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('poly');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // Poly theme
                    const colors = {
                      primary: '#7b3fe4',
                      secondary: '#7b3fe4',
                      background: '#000000',
                      cardBg: '#1a0f2e',
                      text: '#FFFFFF',
                      border: '#7b3fe4',
                      headerAccent: '#7b3fe4'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    // Keep currentThemeName aligned with Header preset theme name
                    setCurrentThemeName('Poly');
                    localStorage.setItem('currentThemeName', 'Poly');
                    
                    // Also set background effect to Particles
                    setBackgroundEffect('particles');
                    localStorage.setItem('backgroundEffect', 'particles');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'particles' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: currentThemeName === 'Poly' ? 'rgba(123, 63, 228, 0.3)' : 'rgba(123, 63, 228, 0.15)',
                    border: currentThemeName === 'Poly' ? '3px solid #7b3fe4' : '2px solid #7b3fe4',
                    borderRadius: '8px',
                    boxShadow: currentThemeName === 'poly' ? '0 0 0 2px rgba(123, 63, 228, 0.5)' : '0 0 0 1px rgba(123, 63, 228, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#7b3fe4',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'poly' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['poly'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['poly']}
                      alt="Poly"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#7b3fe4'
                    }} />
                  )}
                  Poly
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShimmeringTheme('op');
                    setTimeout(() => setShimmeringTheme(null), 800);
                    // OP theme
                    const colors = {
                      primary: '#FF0621',
                      secondary: '#FF0621',
                      background: '#000000',
                      cardBg: '#330000',
                      text: '#FFFFFF',
                      border: '#FF0621',
                      headerAccent: '#FF0621'
                    };
                    saveAndApplyCustomTheme(editingMode, colors);
                    // Keep currentThemeName aligned with Header preset theme name
                    setCurrentThemeName('OP');
                    localStorage.setItem('currentThemeName', 'OP');
                    
                    // Also set background effect to Neural Network
                    setBackgroundEffect('neuralnetwork');
                    localStorage.setItem('backgroundEffect', 'neuralnetwork');
                    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: 'neuralnetwork' }));
                  }}
                  style={{
                    padding: '8px 12px',
                    background: currentThemeName === 'OP' ? 'rgba(255, 6, 33, 0.3)' : 'rgba(255, 6, 33, 0.15)',
                    border: currentThemeName === 'OP' ? '3px solid #FF0621' : '2px solid #FF0621',
                    borderRadius: '8px',
                    boxShadow: currentThemeName === 'op' ? '0 0 0 2px rgba(255, 6, 33, 0.5)' : '0 0 0 1px rgba(255, 6, 33, 0.3)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#FF0621',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    minHeight: '64px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {shimmeringTheme === 'op' && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                      animation: 'shimmer 0.8s ease-out',
                      zIndex: 1
                    }} />
                  )}
                  {PRESET_THEME_LOGOS['op'] ? (
                    <img
                      src={PRESET_THEME_LOGOS['op']}
                      alt="OP"
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#FF0621'
                    }} />
                  )}
                  OP
                </button>
              </div>
            </div>

            {/* Color Pickers */}
            <div style={{ marginBottom: '1rem' }}>
              <h5 style={{
                margin: '0 0 12px 0',
                color: 'var(--color-primary, #0A74FF)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Custom Colors
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {[
                  { key: 'headerAccent', label: 'Primary/Header' },
                  { key: 'text', label: 'Text' },
                  { key: 'background', label: 'Background' }
                ].map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', color: '#999', fontWeight: '500' }}>
                      {label}
                    </label>
                    <input
                      type="color"
                      value={cssColorToHex(String(getCurrentEditingColors()[key as keyof ThemeColors]))}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newColors = {
                          ...getCurrentEditingColors(),
                          [key]: e.target.value
                        };
                        setCustomColors(prev => ({
                          ...prev,
                          [editingMode]: newColors
                        }));
                        saveAndApplyCustomTheme(editingMode, newColors);
                      }}
                      style={{
                        width: '100%',
                        height: '36px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Background Effect Selector */}
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{
                margin: '0 0 12px 0',
                color: 'var(--color-primary, #0A74FF)',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Background Effects
              </h5>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px'
              }}>
                {[
                  { id: 'off', label: 'Off', color: '#666666', emoji: '⊘' },
                  { id: 'matrix', label: 'Matrix', color: '#008F00', emoji: '🔢' },
                  { id: 'smoke', label: 'Smoke', color: '#9CA3AF', emoji: '💨' },
                  { id: 'particles', label: 'Particles', color: '#FCD34D', emoji: '✨' },
                  { id: 'waves', label: 'Waves', color: '#3B82F6', emoji: '🌊' },
                  { id: 'aurora', label: 'Aurora', color: '#A78BFA', emoji: '🌌' },
                  { id: 'orbs', label: 'Orbs', color: '#EC4899', emoji: '🔮' },
                  { id: 'bubbles', label: 'Bubbles', color: '#00D1AE', emoji: '🫧' },
                  { id: 'plasma', label: 'Plasma', color: '#8B5CF6', emoji: '🌀' },
                  { id: 'plasmasmoke', label: 'Plasma Smoke', color: '#5e5e5e', emoji: '🌫️' },
                  { id: 'lightning', label: 'Lightning', color: '#FBBF24', emoji: '⚡' },
                  { id: 'constellation', label: 'Constellation', color: '#60A5FA', emoji: '⭐' },
                  { id: 'cybergrid', label: 'Cyber Grid', color: '#06B6D4', emoji: '🔷' },
                  { id: 'digital', label: 'Digital', color: '#10B981', emoji: '💻' },
                  { id: 'dnahelix', label: 'DNA Helix', color: '#34D399', emoji: '🧬' },
                  { id: 'fireworks', label: 'Fireworks', color: '#F87171', emoji: '🎆' },
                  { id: 'flowfield', label: 'Flow Field', color: '#818CF8', emoji: '🌀' },
                  { id: 'geometric', label: 'Geometric', color: '#F59E0B', emoji: '◆' },
                  { id: 'hexagongrid', label: 'Hexagon Grid', color: '#F3BA2F', emoji: '⬡' },
                  { id: 'neuralnetwork', label: 'Neural Network', color: '#34DA84', emoji: '🧠' },
                  { id: 'pulserings', label: 'Pulse Rings', color: '#A855F7', emoji: '⭕' },
                  { id: 'rain', label: 'Rain', color: '#0EA5E9', emoji: '🌧️' },
                  { id: 'snow', label: 'Snow', color: '#E0E7FF', emoji: '❄️' },
                  { id: 'starfield', label: 'Starfield', color: '#FDE047', emoji: '✨' },
                  { id: 'vortex', label: 'Vortex', color: '#7C3AED', emoji: '🌀' }
                ].map(effect => (
                  <button
                    key={effect.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShimmeringTheme(`effect-${effect.id}`);
                      setTimeout(() => setShimmeringTheme(null), 800);
                      setBackgroundEffect(effect.id);
                      localStorage.setItem('backgroundEffect', effect.id);
                      window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: effect.id }));
                    }}
                    style={{
                      padding: '8px 12px',
                      background: backgroundEffect === effect.id 
                        ? `rgba(${parseInt(effect.color.slice(1,3), 16)}, ${parseInt(effect.color.slice(3,5), 16)}, ${parseInt(effect.color.slice(5,7), 16)}, 0.3)`
                        : `rgba(${parseInt(effect.color.slice(1,3), 16)}, ${parseInt(effect.color.slice(3,5), 16)}, ${parseInt(effect.color.slice(5,7), 16)}, 0.15)`,
                      border: backgroundEffect === effect.id ? `3px solid ${effect.color}` : `2px solid ${effect.color}`,
                      borderRadius: '8px',
                      boxShadow: backgroundEffect === effect.id 
                        ? `0 0 0 2px rgba(${parseInt(effect.color.slice(1,3), 16)}, ${parseInt(effect.color.slice(3,5), 16)}, ${parseInt(effect.color.slice(5,7), 16)}, 0.5)`
                        : `0 0 0 1px rgba(${parseInt(effect.color.slice(1,3), 16)}, ${parseInt(effect.color.slice(3,5), 16)}, ${parseInt(effect.color.slice(5,7), 16)}, 0.3)`,
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: effect.color,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      minHeight: '40px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {shimmeringTheme === `effect-${effect.id}` && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 0.8s ease-out',
                        zIndex: 1
                      }} />
                    )}
                    <span style={{ fontSize: '14px' }}>{effect.emoji}</span>
                    {effect.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reset App Theme */}
            <div style={{ marginBottom: '20px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetTheme();
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                Reset App Theme
              </button>
            </div>
          </div>

          {/* Copyright Notice */}
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            borderTop: `1px solid ${customColors[currentAppTheme as 'dark' | 'light'].border}`,
            fontSize: '11px',
            color: customColors[currentAppTheme as 'dark' | 'light'].text
          }}>
            <div style={{ marginBottom: '4px', opacity: 0.7 }}>Courtesy of</div>
            <a 
              href="https://aifactcheck.app" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                fontWeight: '600', 
                color: customColors[currentAppTheme as 'dark' | 'light'].headerAccent,
                textDecoration: 'none',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              © AiFactCheck.App
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwatchPro;
